<template>
  <div class="modal fade" id="companyChargesModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg" style="max-width: 80%;">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Cobranças - {{ company?.company_name }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- Info da empresa -->
          <div class="d-flex align-items-center bg-light-primary rounded p-3 mb-4" v-if="company">
            <div class="me-4">
              <i class="bi bi-building fs-2 text-primary"></i>
            </div>
            <div class="flex-grow-1">
              <div class="fw-bold fs-5 text-dark">{{ company.company_name }}</div>
              <div class="d-flex gap-4 mt-1">
                <span class="text-gray-700 fs-6"><strong>CNPJ:</strong> {{ company.formatted_cnpj }}</span>
              </div>
            </div>
          </div>

          <!-- Tabela de cobranças -->
          <div class="table-responsive">
            <table class="table table-hover table-row-bordered">
              <thead>
              <tr class="fw-bold fs-6 text-muted">
                <th>Referência</th>
                <th>Plano</th>
                <th>Contrato</th>
                <th>Valor</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Pagamento</th>
                <th class="text-center">Ações</th>
              </tr>
              </thead>
              <tbody>
              <template v-for="charge in charges" :key="charge.id_company_charge">
                <tr>
                <td>{{ charge.charge_reference }}</td>
                <td>{{ charge.billing_closing?.contract?.plan?.plan_name || '—' }}</td>
                <td>{{ charge.billing_closing?.contract?.contract_number || '—' }}</td>
                <td>R$ {{ formatMoney(charge.charge_amount) }}</td>
                <td>{{ formatDate(charge.charge_due_date) }}</td>
                <td>
                  <span :class="getStatusClass(charge.charge_status)">
                    {{ charge.charge_status_dsc }}
                  </span>
                </td>
                <td>
                  <span v-if="charge.charge_paid_date">{{ formatDateTime(charge.charge_paid_date) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center" style="min-width: 220px;">
                  <template v-if="charge.charge_status !== 'cancelled'">
                    <a v-if="charge.boleto_url" :href="charge.boleto_url" target="_blank"
                       class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                       title="Baixar boleto">
                      <i class="bi bi-file-earmark-pdf"></i>
                    </a>
                    <button v-if="hasBoletoFailure(charge)"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-warning me-1 border border-gray-400"
                            type="button"
                            @click="reprocessBoleto(charge)"
                            title="Reprocessar boleto">
                      <i class="bi bi-arrow-repeat"></i>
                    </button>
                    <button v-if="canResendEmail(charge)"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-info me-1 border border-gray-400"
                            type="button"
                            @click="resendEmail(charge)"
                            title="Reenviar boleto e NFSe por e-mail">
                      <i class="bi bi-envelope"></i>
                    </button>
                    <template v-if="charge.id_nfse && charge.nfse">
                      <span v-if="charge.nfse.nfse_status === 'rejected' || charge.nfse_error_message"
                            class="me-1"
                            :title="charge.nfse.nfse_status_message || charge.nfse_error_message || 'NFSe rejeitada'">
                        <i class="bi bi-exclamation-triangle text-warning"></i>
                      </span>
                      <a v-if="isNfseAuthorized(charge)"
                         href="#"
                         class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                         title="Baixar NFSe"
                         @click.prevent="downloadNfseZip(charge)">
                        <i class="bi bi-file-earmark-zip"></i>
                      </a>
                    </template>
                    <button v-if="hasNfseFailureOrRetry(charge)"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-warning me-1 border border-gray-400"
                            type="button"
                            @click="reprocessNfse(charge)"
                            title="Reprocessar NFSe">
                      <i class="bi bi-arrow-repeat"></i>
                    </button>
                    <button v-if="charge.charge_status !== 'paid'"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-success me-1 border border-gray-400"
                            type="button" @click="markPaid(charge)" title="Marcar como pago">
                      <i class="bi bi-check-circle"></i>
                    </button>
                    <button v-if="charge.charge_status === 'paid'"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-warning me-1 border border-gray-400"
                            type="button" @click="unmarkPaid(charge)" title="Desfazer pagamento">
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>
                    <button v-if="canCancelCharge(charge)"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger me-1 border border-gray-400"
                            type="button"
                            @click="cancelCharge(charge)"
                            title="Cancelar cobrança">
                      <i class="bi bi-x-circle"></i>
                    </button>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
              <tr v-if="hasChargeErrors(charge)" class="bg-danger bg-opacity-10">
                <td colspan="8" class="py-2 small text-danger">
                  <span v-if="charge.boleto_error_message" class="d-inline-block me-3">
                    <i class="bi bi-exclamation-circle me-1"></i> Falha no boleto: {{ charge.boleto_error_message }}
                  </span>
                  <span v-if="charge.nfse_error_message" class="d-inline-block">
                    <i class="bi bi-exclamation-circle me-1"></i> Falha na NFSe: {{ charge.nfse_error_message }}
                  </span>
                </td>
              </tr>
              </template>
              <tr v-if="charges.length === 0">
                <td colspan="8" class="text-center text-muted py-5">Nenhuma cobrança encontrada.</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Fechar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from "@/helpers/modal";
import Alert from "@/helpers/alert";
import Loader from "@/helpers/loader";
import { CompanyService } from "@/service/companyService";

export default {
  name: "CompanyChargesModal",
  props: ['company'],
  emits: ['onClose'],
  data() {
    return {
      charges: [],
      companyService: CompanyService.new(),
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadCharges();
        } else {
          this.charges = [];
        }
      }
    }
  },
  methods: {
    date: moment,
    formatDate(date) {
      if (!date) return '-';
      return this.date(date).format('DD/MM/YYYY');
    },
    formatDateTime(date) {
      if (!date) return '-';
      return this.date(date).format('DD/MM/YYYY HH:mm');
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2).replace('.', ',');
    },
    getStatusClass(status) {
      const classes = {
        pending: 'badge badge-warning',
        paid: 'badge badge-success',
        overdue: 'badge badge-danger',
        cancelled: 'badge badge-secondary',
      };
      return classes[status] || 'badge badge-light';
    },
    loadCharges() {
      if (!this.company) return;

      this.companyService.charges(this.place.id_place, this.company.id_company)
        .then((response) => {
          this.charges = response.data?.data || response.data || [];
        });
    },
    markPaid(charge) {
      Swal.fire({
        title: 'Confirmar pagamento',
        text: `Marcar a cobrança de R$ ${this.formatMoney(charge.charge_amount)} como paga?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, marcar como pago',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Atualizando cobrança...');
          this.companyService.markChargePaid(this.place.id_place, this.company.id_company, charge.id_company_charge)
            .then((response) => {
              Loader.close();
              const updatedCharge = response.data?.data || response.data;
              const index = this.charges.findIndex(c => c.id_company_charge === charge.id_company_charge);
              if (index !== -1) {
                this.charges[index] = { ...this.charges[index], ...updatedCharge };
              }
              Alert.success('Cobrança marcada como paga.');
            })
            .catch((error) => {
              Loader.close();
              Alert.error(error.response?.data?.message || 'Erro ao atualizar cobrança.');
            });
        }
      });
    },
    unmarkPaid(charge) {
      Swal.fire({
        title: 'Desfazer pagamento',
        text: 'Tem certeza que deseja desfazer o pagamento desta cobrança?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, desfazer',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ffc107',
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Atualizando cobrança...');
          this.companyService.unmarkChargePaid(this.place.id_place, this.company.id_company, charge.id_company_charge)
            .then((response) => {
              Loader.close();
              const updatedCharge = response.data?.data || response.data;
              const index = this.charges.findIndex(c => c.id_company_charge === charge.id_company_charge);
              if (index !== -1) {
                this.charges[index] = { ...this.charges[index], ...updatedCharge };
              }
              Alert.success('Pagamento desfeito.');
            })
            .catch((error) => {
              Loader.close();
              Alert.error(error.response?.data?.message || 'Erro ao atualizar cobrança.');
            });
        }
      });
    },
    isNfseAuthorized(charge) {
      return charge.nfse && (charge.nfse.nfse_status === 'authorized' || charge.nfse.nfse_status === 'corrected');
    },
    isNfseRejectedOrDraft(charge) {
      return charge.nfse && (charge.nfse.nfse_status === 'rejected' || charge.nfse.nfse_status === 'draft');
    },
    downloadNfseZip(charge) {
      Loader.show('Gerando ZIP da NFSe...');
      this.companyService.downloadChargeNfseZip(this.place.id_place, this.company.id_company, charge.id_company_charge)
        .then((response) => {
          Loader.close();
          const blob = response.data;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `nfse_${(charge.charge_reference || '').replace('/', '_')}.zip`;
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          Loader.close();
          Alert.error(e.response?.data?.message || 'Erro ao baixar NFSe.');
        });
    },
    hasNfseFailureOrRetry(charge) {
      if (charge.charge_status === 'cancelled') return false;
      if (charge.nfse_error_message) return true;
      return charge.nfse && (charge.nfse.nfse_status === 'rejected' || charge.nfse.nfse_status === 'draft');
    },
    reprocessNfse(charge) {
      Loader.show('Reprocessando NFSe...');
      this.companyService.reprocessNfse(this.place.id_place, this.company.id_company, charge.id_company_charge)
        .then(() => {
          Loader.close();
          Alert.success('Reprocessamento da NFSe em andamento.');
          
          // Limpar mensagem de erro localmente imediatamente
          const index = this.charges.findIndex(c => c.id_company_charge === charge.id_company_charge);
          if (index !== -1) {
            this.charges[index] = { ...this.charges[index], nfse_error_message: null };
          }
          
          // Recarregar lista após um delay para pegar os dados atualizados
          setTimeout(() => {
            this.loadCharges();
          }, 2000);
        })
        .catch((e) => {
          Loader.close();
          Alert.error(e.response?.data?.message || 'Erro ao reprocessar NFSe.');
        });
    },
    canResendEmail(charge) {
      return this.company?.company_email && (charge.boleto_url || charge.id_nfse);
    },
    resendEmail(charge) {
      Loader.show('Reenviando e-mail...');
      this.companyService.resendChargeEmail(this.place.id_place, this.company.id_company, charge.id_company_charge)
        .then(() => {
          Loader.close();
          Alert.success('E-mail reenviado com sucesso para ' + (this.company?.company_email || ''));
        })
        .catch((e) => {
          Loader.close();
          Alert.error(e.response?.data?.message || 'Erro ao reenviar e-mail.');
        });
    },
    canCancelCharge(charge) {
      return charge.charge_status === 'pending' || charge.charge_status === 'overdue';
    },
    hasBoletoFailure(charge) {
      return charge.charge_status !== 'cancelled' && charge.boleto_error_message;
    },
    hasChargeErrors(charge) {
      return charge.boleto_error_message || charge.nfse_error_message;
    },
    reprocessBoleto(charge) {
      Loader.show('Reprocessando boleto...');
      this.companyService.reprocessBoleto(this.place.id_place, this.company.id_company, charge.id_company_charge)
        .then(() => {
          Loader.close();
          Alert.success('Reprocessamento do boleto em andamento.');
          
          // Limpar mensagem de erro localmente imediatamente
          const index = this.charges.findIndex(c => c.id_company_charge === charge.id_company_charge);
          if (index !== -1) {
            this.charges[index] = { ...this.charges[index], boleto_error_message: null };
          }
          
          // Recarregar lista após um delay para pegar os dados atualizados
          setTimeout(() => {
            this.loadCharges();
          }, 2000);
        })
        .catch((e) => {
          Loader.close();
          Alert.error(e.response?.data?.message || 'Erro ao reprocessar boleto.');
        });
    },
    cancelCharge(charge) {
      Swal.fire({
        title: 'Cancelar cobrança',
        text: `Tem certeza que deseja cancelar a cobrança de R$ ${this.formatMoney(charge.charge_amount)} (${charge.charge_reference})?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, cancelar',
        cancelButtonText: 'Não',
        confirmButtonColor: '#dc3545',
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Cancelando cobrança...');
          this.companyService.cancelCharge(this.place.id_place, this.company.id_company, charge.id_company_charge)
            .then((response) => {
              Loader.close();
              const updatedCharge = response.data?.data || response.data;
              const index = this.charges.findIndex(c => c.id_company_charge === charge.id_company_charge);
              if (index !== -1) {
                this.charges[index] = { ...this.charges[index], ...updatedCharge };
              }
              Alert.success('Cobrança cancelada.');
            })
            .catch((e) => {
              Loader.close();
              Alert.error(e.response?.data?.message || 'Erro ao cancelar cobrança.');
            });
        }
      });
    },
    closeModal() {
      Modal.toggle('#companyChargesModal');
      this.$emit('onClose');
    },
  }
}
</script>
